import { FC, useEffect, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import Table from '../../common/Table'
import TableHeader from '../../common/TableHeader'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../../../../redux/store'
import { deleteProductApi, getProductListApi } from '../../../../../redux/slices/ProductSlice'
import { toast } from 'react-toastify'

const ProductTable: FC = () => {
    const [loading, setLoading] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [searchKey, setSearchKey] = useState('')
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()

    const productData = useSelector((state: RootState) => state.product.ProductList?.data?.data || [])
    const productTotal = useSelector((state: RootState) => state.product.ProductList?.data?.total || 0)

    useEffect(() => {
        const debounceTimeout = setTimeout(() => {
            setDebouncedSearchQuery(searchKey)
        }, 1000)

        return () => clearTimeout(debounceTimeout)
    }, [searchKey])

    const fetchProductList = () => {
        setLoading(true)
        dispatch(
            getProductListApi({
                pageNumber: currentPage,
                pageSize: rowsPerPage,
                filterkey: debouncedSearchQuery,
            })
        )
            .then(() => {
                setLoading(false)
            })
            .catch(() => {
                setLoading(false)
                toast.error('Failed to fetch product data')
            })
    }

    useEffect(() => {
        fetchProductList()
    }, [currentPage, rowsPerPage, debouncedSearchQuery])

    const handleEdit = (id: any) => {
        navigate(`edit/${id}`)
    }

    const handleDelete = (id: any) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            dispatch(deleteProductApi(id)).then((response: any) => {
                if (response.payload.success) {
                    toast.success(response.payload.reason)
                    fetchProductList()
                } else {
                    toast.error(response.payload.reason || 'Something went wrong')
                }
            })
        }
    }

    const handleSearchChange = (searchQuery: string) => {
        setSearchKey(searchQuery)
    }

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
    }

    const handlePerRowsChange = (newPerPage: number, page: number) => {
        setCurrentPage(page)
        setRowsPerPage(newPerPage)
    }

    const columnData = [
        {
            name: 'Product',
            cell: (row: any) => {
                const imageUrl = row?.product_image_url || row?.product_image
                return (
                    <div className='d-flex align-items-center py-3'>
                        <div className='symbol symbol-50px me-3'>
                            {imageUrl ? (
                                <img
                                    src={imageUrl}
                                    alt={row?.name}
                                    style={{ borderRadius: '8px', objectFit: 'cover', width: '50px', height: '50px' }}
                                />
                            ) : (
                                <div
                                    className='symbol-label bg-light-primary'
                                    style={{
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '50px',
                                        height: '50px',
                                    }}
                                >
                                    <i className='fas fa-box fs-2 text-primary' />
                                </div>
                            )}
                        </div>
                        <div className='d-flex flex-column'>
                            <span className='text-dark fw-bold fs-6 mb-1'>{row?.name || '-'}</span>
                            <span className='text-muted fw-semibold fs-7'>SKU: {row?.sku || '-'}</span>
                        </div>
                    </div>
                )
            },
            minWidth: '250px',
            sortable: true,
        },
        {
            name: 'Price',
            cell: (row: any) => (
                <span className='text-dark fw-semibold fs-7'>
                    {row?.price ? `₹${Number(row.price).toFixed(2)}` : '0.00'}
                </span>
            ),
            minWidth: '100px',
            sortable: true,
        },
        {
            name: 'Stock',
            cell: (row: any) => (
                <span className={`badge ${row?.stock > 0 ? 'badge-light-success' : 'badge-light-danger'} fw-bold`}>
                    {row?.stock || 0}
                </span>
            ),
            minWidth: '100px',
            sortable: true,
        },
        {
            name: 'Status',
            cell: (row: any) => (
                <span className={`badge ${row?.is_active ? 'badge-light-success' : 'badge-light-danger'} fw-bold`}>
                    {row?.is_active ? 'Active' : 'Inactive'}
                </span>
            ),
            minWidth: '100px',
            sortable: true,
        },
        {
            name: 'Actions',
            cell: (row: any) => (
                <div className='d-flex align-items-center gap-2 py-2'>
                    <OverlayTrigger placement='top' overlay={<Tooltip>Edit Product</Tooltip>}>
                        <button
                            className='btn btn-icon btn-light-primary btn-sm'
                            onClick={() => handleEdit(row?.id)}
                            style={{
                                width: '35px',
                                height: '35px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '6px',
                            }}
                        >
                            <i className='bi bi-pencil fs-5'></i>
                        </button>
                    </OverlayTrigger>

                    <OverlayTrigger placement='top' overlay={<Tooltip>Delete Product</Tooltip>}>
                        <button
                            className='btn btn-icon btn-light-danger btn-sm'
                            onClick={() => handleDelete(row?.id)}
                            style={{
                                width: '35px',
                                height: '35px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '6px',
                            }}
                        >
                            <i className='bi bi-trash fs-5'></i>
                        </button>
                    </OverlayTrigger>
                </div>
            ),
            minWidth: '150px',
            sortable: false,
        },
    ]

    return (
        <div className='card shadow-sm'>
            <TableHeader
                search={searchKey}
                addButtonText={'Add Product'}
                handleSearchChange={handleSearchChange}
                handleAddButton={() => navigate('add')}
            />

            <div className='card-body p-0 shadow-sm'>
                <Table
                    data={productData}
                    columns={columnData}
                    isLoading={loading}
                    handlePerRowsChange={handlePerRowsChange}
                    handlePageChange={handlePageChange}
                    totalRows={productTotal}
                    perPageData={rowsPerPage}
                    showPagination={true}
                    onRowClicked={() => { }}
                />
            </div>
        </div>
    )
}

export default ProductTable
